import Debounce from '../Debounce/debounce.min.js';

/**
 * @typedef {Object<string, any>} FocusArea
 * @property {number} x Horizontal position of the focus area.
 * @property {number} y Vertical position of the focus area.
 * @property {number} fw (FocusWidth) Width of the focus area.
 * @property {number} fh (FocusHeight) Height of the focus area.
 * @property {number} ih (ImageHeight) Height of the original image used to calculate the image size on background cover.
 * @property {number} iw (ImageWidth) Width of the original image used to calculate the image size on background cover.
 */

/**
 * @property {FocusArea[]} options
 * @property {FocusArea} focusArea
 * @property {Object} container
 * @property {boolean} isFixed
 * @property {Object} boundingClientRect
 * @property {boolean} immediately
 */
class BackgroundFocus {

	/**
   * @param {Object} container
   * @param {FocusArea[]} options
   * @param {boolean} immediately
   */
	constructor(container, options, immediately= false) {
		this.container = container;
		this.options = options;
		this.isFixed = false;
		this.boundingClientRect = null;
		this.immediately = immediately;

		this.initialize();
	}

	initialize() {
		if(document.readyState === 'interactive' || document.readyState === 'complete' || document.readyState === 'loaded') {
			this.updateFocusAreaByMediaCondition();
			this.updateContainerBackgroundPosition();
		} else {
		  if(this.immediately) {
        this.updateFocusAreaByMediaCondition();
        this.updateContainerBackgroundPosition();
      }
			document.addEventListener('readystatechange', () => {
        if(document.readyState === 'interactive' || document.readyState === 'complete' || document.readyState === 'loaded') {
          this.updateFocusAreaByMediaCondition();
          this.updateContainerBackgroundPosition();
        }
      });
		}
	}

	updateContainerBackgroundPosition() {
		this.isFixed = getComputedStyle(this.container).backgroundAttachment === 'fixed';
		this.boundingClientRect = this.container.getBoundingClientRect();

		const containerBounds = this.getContainerBounds();
		const imageSize = this.calcBackgroundImageCoverSize(containerBounds, this.focusArea.iw, this.focusArea.ih);

		const maxX = this.isFixed ? Math.floor(this.boundingClientRect.left) : 0;
		const maxY = 0;
		const minX = (imageSize.width - containerBounds.width) * -1;
		const minY = (imageSize.height - containerBounds.height) * -1;

		const backgroundPosition = {
			x: imageSize.width * this.focusArea.x * -1 - imageSize.width * this.focusArea.fw / 2 + containerBounds.width / 2,
			y: imageSize.height * this.focusArea.y * -1 - imageSize.height * this.focusArea.fh / 2 + containerBounds.height / 2,
		};

		backgroundPosition.x = this.range(backgroundPosition.x, minX, maxX);
		backgroundPosition.y = this.range(backgroundPosition.y, minY, maxY);

		this.container.style.backgroundPosition = backgroundPosition.x + 'px ' + backgroundPosition.y + 'px';
	}

	getContainerBounds() {
		return this.isFixed ? {width: window.innerWidth, height: window.innerHeight} : this.boundingClientRect;
	}

	/**
	 * @param number
	 * @param min
	 * @param max
	 * @returns {number}
	 */
	range(number, min, max) {
		return number < min ? min : number > max ? max : number;
	}

	/**
	 * @param boundingRect
	 * @param originalImageWidth
	 * @param originalImageHeight
	 *
	 * @returns {number[]}
	 */
	calcBackgroundImageCoverSize(boundingRect, originalImageWidth, originalImageHeight) {
		const imageCoverSize = [];
		const rectWidth = this.isFixed ? window.innerWidth : boundingRect.width;
		const rectHeight = this.isFixed ? window.innerHeight : boundingRect.height;

		if (100 / rectWidth * (rectWidth - originalImageWidth) * -1 < 100 / rectHeight * (rectHeight - originalImageHeight) * -1) {
			imageCoverSize['width'] = rectWidth;
			imageCoverSize['height'] = originalImageHeight - (originalImageHeight / originalImageWidth * ((rectWidth - originalImageWidth) * -1));
		} else {
			imageCoverSize['height'] = rectHeight;
			imageCoverSize['width'] = originalImageWidth - (originalImageWidth / originalImageHeight * ((rectHeight - originalImageHeight) * -1));
		}

		return imageCoverSize;
	}

	/**
	 * @returns {FocusArea|Boolean}
	 */
	updateFocusAreaByMediaCondition() {
		this.focusArea = false;

		for (let mediaCondition in this.options) {
			if (this.options.hasOwnProperty(mediaCondition)) {
				const mediaConditionResult = window.matchMedia(mediaCondition);
				if (mediaConditionResult && mediaConditionResult.matches || mediaCondition === 'default') {
					this.focusArea = this.options[mediaCondition];

					if (mediaCondition !== 'default') {
						break;
					}
				}
			}
		}

		return this.focusArea;
	}

	/**
   * @param {Object|Element} container
   * @param {FocusArea[]} options
   * @param {boolean} immediately
   */
	static observe(container, options, immediately = false) {
		if (!container || !options || getComputedStyle(container).backgroundSize !== 'cover' || BackgroundFocus.observables.has(container)) {
			return;
		}
		const instance = new this(container, options, immediately);
    BackgroundFocus.observables.set(container, instance);
    BackgroundFocus.observer.observe(container)

		if (!BackgroundFocus.observes) {
			BackgroundFocus.observes = true;

			window.addEventListener('resize', Debounce.debounce(() => {
				for (let index = 0; index < BackgroundFocus.observables.length; index++) {
          BackgroundFocus.observables.forEach(observable => {
            observable.updateContainerBackgroundPosition();
          })
				}
			}, 500));
		}

		return instance;
	}

  static createObserver() {
    if (!window.ResizeObserver) {
      return false
    }
    return new ResizeObserver(entries => {
      for (let entry of entries) {
        const instance = BackgroundFocus.observables.get(entry.target)
        instance.updateContainerBackgroundPosition()
      }
    })
  }
}

BackgroundFocus.observer = BackgroundFocus.createObserver();
BackgroundFocus.observes = !!BackgroundFocus.observer;
BackgroundFocus.observables = new Map;
BackgroundFocus.resizeObserver = null;

export default BackgroundFocus;
